import React from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { CheckCircle2 } from "lucide-react";
import { formatCurrency } from "@/utils/admin/formatters";

// Configuração de cenários e percentuais
const scenarios = [10_000, 100_000, 500_000, 1_000_000]; // Faturamento mensal (R$)
const savingRate = 0.054; // 5,4% ao mês (adequado ao layout de referência)
const restitutionMonths = 108; // Média de restituição (108 meses)

const SavingsComparison: React.FC = () => {
  return (
    <section
      className="py-8 md:py-16 px-4 md:px-6 bg-gradient-to-b from-accent to-background"
      aria-labelledby="savings-comparison-heading"
    >
      <div className="container mx-auto max-w-5xl">
        <header className="text-center mb-6 md:mb-10">
          <p className="text-xs tracking-widest font-medium uppercase text-primary mb-2">
            Conheça a Tax Health
          </p>
          <h2
            id="savings-comparison-heading"
            className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground"
          >
            Veja sua <span className="text-primary">economia</span>
          </h2>
        </header>

        <div className="space-y-4 md:space-y-6">
          {scenarios.map((monthlyRevenue) => {
            const monthly = monthlyRevenue * savingRate;
            const annual = monthly * 12;
            const fiveYears = monthly * 60;
            const tenYears = monthly * 120;
            const restitution = monthly * restitutionMonths;

            return (
              <Card
                key={monthlyRevenue}
                className="shadow-sm hover:shadow-md transition-shadow rounded-2xl"
                aria-label={`Cenário de faturamento mensal ${formatCurrency(monthlyRevenue)}`}
              >
                <CardContent className="p-4 md:p-6">
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_1.5fr] gap-6 items-start">
                    {/* Coluna esquerda: descrição do cenário */}
                    <div>
                      <h3 className="text-lg md:text-xl font-semibold text-primary">
                        Faturamento mensal de
                        <br />
                        <span className="text-xl md:text-2xl text-foreground">{formatCurrency(monthlyRevenue)}</span>
                      </h3>
                      <p className="text-sm text-muted-foreground mt-2">
                        Veja quanto economiza nesse cenário:
                      </p>
                    </div>

                    {/* Coluna direita: lista de economias */}
                    <div>
                      <ul className="space-y-3">
                        <li>
                          <div className="flex items-center justify-between text-foreground">
                            <span className="flex items-center gap-2 text-sm">
                              <CheckCircle2 className="h-4 w-4 text-primary" aria-hidden="true" />
                              Mensal
                            </span>
                            <span className="font-medium">{formatCurrency(monthly)}</span>
                          </div>
                          <Separator className="my-3" />
                        </li>

                        <li>
                          <div className="flex items-center justify-between text-foreground">
                            <span className="flex items-center gap-2 text-sm">
                              <CheckCircle2 className="h-4 w-4 text-primary" aria-hidden="true" />
                              Anual
                            </span>
                            <span className="font-medium">{formatCurrency(annual)}</span>
                          </div>
                          <Separator className="my-3" />
                        </li>

                        <li>
                          <div className="flex items-center justify-between text-foreground">
                            <span className="flex items-center gap-2 text-sm">
                              <CheckCircle2 className="h-4 w-4 text-primary" aria-hidden="true" />
                              5 anos
                            </span>
                            <span className="font-medium">{formatCurrency(fiveYears)}</span>
                          </div>
                          <Separator className="my-3" />
                        </li>

                        <li>
                          <div className="flex items-center justify-between text-foreground">
                            <span className="flex items-center gap-2 text-sm">
                              <CheckCircle2 className="h-4 w-4 text-primary" aria-hidden="true" />
                              10 anos
                            </span>
                            <span className="font-medium">{formatCurrency(tenYears)}</span>
                          </div>
                          <Separator className="my-3" />
                        </li>

                        <li>
                          <div className="flex items-center justify-between text-foreground">
                            <span className="flex items-center gap-2 font-semibold">
                              <CheckCircle2 className="h-4 w-4 text-primary" aria-hidden="true" />
                              Média de restituição
                            </span>
                            <span className="font-semibold">{formatCurrency(restitution)}</span>
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
        <div className="mt-8 md:mt-10 flex justify-center">
          <Button asChild size="lg" className="min-h-[48px]">
            <Link to="/calcule-sua-restituicao">Calcule sua restituição</Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default SavingsComparison;
